import React from 'react';
import '../../../../components/skeleton/skeleton.css';

/**
 * 페이지별 DB/API 로딩 시 공통 스켈레톤 UI
 * ContentSkeleton과 동일한 skeleton-line(shimmer) 방식 사용
 *
 * @param {Object} props
 * @param {'cardList'|'grid'|'table'|'content'} [props.variant='cardList'] - 레이아웃 타입
 * @param {number} [props.cardCount=5] - 카드/행 개수 (cardList, table, content)
 * @param {boolean} [props.showTabs=true] - cardList일 때 탭 영역 스켈레톤 표시 여부
 * @param {string} [props.className] - 컨테이너 추가 클래스
 * @param {string} [props.minHeight='60vh'] - 최소 높이
 */
const PageLoadingSkeleton = ({
  variant = 'cardList',
  cardCount = 5,
  showTabs = true,
  className = '',
  minHeight = '60vh'
}) => {
  const containerClass = `page-loading-skeleton page-loading-skeleton--${variant} ${className}`.trim();

  // 탭 영역 (cardList 전용)
  const renderTabs = () => (
    <div className="col-12 mb-4">
      <div className="d-flex gap-2 flex-wrap mb-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeleton-line" style={{ width: '140px', height: '40px', borderRadius: '999px' }} />
        ))}
      </div>
    </div>
  );

  // 카드 목록 블록 (카드 N개)
  const renderCardListBlock = () => (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="skeleton-line" style={{ width: '30%', height: '24px' }} />
          <div className="skeleton-line" style={{ width: '80px', height: '36px', borderRadius: '8px' }} />
        </div>
        <div className="d-flex flex-column gap-4">
          {Array.from({ length: cardCount }, (_, item) => (
            <div key={item} className="border rounded-3 p-4" style={{ borderColor: '#e5e7eb' }}>
              <div className="d-flex gap-2 mb-3 flex-wrap">
                <div className="skeleton-line" style={{ width: '60px', height: '22px', borderRadius: '6px' }} />
                <div className="skeleton-line" style={{ width: '50px', height: '22px', borderRadius: '6px' }} />
              </div>
              <div className="skeleton-line mb-3" style={{ width: '70%', height: '20px' }} />
              <div className="skeleton-line mb-2" style={{ width: '90%', height: '16px' }} />
              <div className="skeleton-line mb-3" style={{ width: '60%', height: '16px' }} />
              <div className="d-flex gap-3 flex-wrap">
                {[1, 2, 3, 4].map((m) => (
                  <div key={m} className="skeleton-line" style={{ width: '120px', height: '14px' }} />
                ))}
              </div>
              <div className="mt-3">
                <div className="skeleton-line" style={{ width: '100px', height: '38px', borderRadius: '8px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // 그리드 카드 (4열)
  const renderGridBlock = () => (
    <div className="row g-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <div key={item} className="col-12 col-md-6 col-lg-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="skeleton-line mb-3" style={{ width: '50%', height: '20px' }} />
              <div className="skeleton-line mb-2" style={{ width: '80%', height: '16px' }} />
              <div className="skeleton-line" style={{ width: '60%', height: '16px' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // 테이블 행
  const renderTableBlock = () => (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <div className="skeleton-line mb-4" style={{ width: '30%', height: '24px' }} />
        <div className="skeleton-table">
          {Array.from({ length: cardCount }, (_, row) => (
            <div key={row} className="skeleton-table-row d-flex mb-3">
              <div className="skeleton-line" style={{ width: '20%', height: '20px', marginRight: '16px' }} />
              <div className="skeleton-line" style={{ width: '30%', height: '20px', marginRight: '16px' }} />
              <div className="skeleton-line" style={{ width: '25%', height: '20px', marginRight: '16px' }} />
              <div className="skeleton-line" style={{ width: '25%', height: '20px' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // content: 그리드 + 테이블 (ContentSkeleton 스타일)
  const renderContentBlock = () => (
    <>
      <div className="row g-4">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="col-12 col-md-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="skeleton-line mb-3" style={{ width: '50%', height: '20px' }} />
                <div className="skeleton-line mb-2" style={{ width: '80%', height: '16px' }} />
                <div className="skeleton-line" style={{ width: '60%', height: '16px' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="card mt-4">
        <div className="card-body">
          <div className="skeleton-line mb-4" style={{ width: '30%', height: '24px' }} />
          <div className="skeleton-table">
            {[1, 2, 3, 4, 5].map((row) => (
              <div key={row} className="skeleton-table-row d-flex mb-3">
                <div className="skeleton-line" style={{ width: '20%', height: '20px', marginRight: '16px' }} />
                <div className="skeleton-line" style={{ width: '30%', height: '20px', marginRight: '16px' }} />
                <div className="skeleton-line" style={{ width: '25%', height: '20px', marginRight: '16px' }} />
                <div className="skeleton-line" style={{ width: '25%', height: '20px' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const renderBody = () => {
    switch (variant) {
      case 'grid':
        return renderGridBlock();
      case 'table':
        return renderTableBlock();
      case 'content':
        return renderContentBlock();
      case 'cardList':
      default:
        return renderCardListBlock();
    }
  };

  return (
    <div className={containerClass} style={{ minHeight }}>
      {variant === 'cardList' && showTabs && renderTabs()}
      {renderBody()}
      <style>{`
        .page-loading-skeleton { min-height: 60vh; }
      `}</style>
    </div>
  );
};

export default PageLoadingSkeleton;
